import { Text, Button, Modal, LinkButton, Tooltip } from '$components'
import { FolderTagIcon, InfoIcon, PlusIcon } from '$components'
import { Step } from '$components'
import { useState } from 'react'
import { ServiceRoutePath } from '../routes/protected'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { PermissionsHelper } from '$utils/permissions'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { useAuth } from '$features/auth/hooks/use-auth'
import { Role } from '$api-client/types.gen'

export type NoServicesEmptyStateProps = {
  isNoResults: boolean
}

export function NoServicesEmptyState({ isNoResults = false }: NoServicesEmptyStateProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { hasSubscriptionExpired } = useTenant()
  const { user } = useAuth()

  const isCreateButtonDisabled = hasSubscriptionExpired || PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
  const tooltipContent = hasSubscriptionExpired
    ? SUBSCRIPTION_EXPIRED_MESSAGE
    : PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
      ? 'You are not allowed to create new services.'
      : undefined

  return (
    <>
      <div className="w-full h-fit p-stack-hero bg-surface-muted rounded-card flex flex-col items-center justify-center text-center">
        <FolderTagIcon className="size-12 text-fg-link mb-stack-roomy" />

        <Text
          type="h3"
          className="mb-stack-roomy text-fg"
        >
          {!isNoResults ? 'No services created yet' : 'No services found'}
        </Text>

        <Text
          type="s"
          className="text-fg-subtle max-w-prose-md mb-stack-section"
        >
          {!isNoResults
            ? 'Services are the offerings you provide to your clients. Each service can have multiple variants for different breed types, with specific pricing and duration.'
            : 'No services found matching your search. Try adjusting your search criteria or create a new service.'
          }
        </Text>

        {!isNoResults
          ? (<Button
            variant="ghost"
            icon={InfoIcon}
            onClick={() => setIsModalOpen(true)}
          >
            How do services work?
          </Button>
          )
          : (
            <Tooltip
              content={tooltipContent}
              disabled={!isCreateButtonDisabled}
            >
              <LinkButton
                to={ServiceRoutePath.ServiceCreate}
                icon={PlusIcon}
                disabled={isCreateButtonDisabled}
              >
                New Service
              </LinkButton>
            </Tooltip>
          )
        }
      </div>

      {isModalOpen && (
        <Modal onClose={() => setIsModalOpen(false)}>
          <Modal.Header>
            <Text
              type="h2"
              as="h1"
            >
              What are services?
            </Text>

            <Text
              type="s"
              className="text-fg-subtle"
            >
              Everything you need to know about managing your services
            </Text>
          </Modal.Header>

          <Modal.Content>
            <div className="space-y-stack-tight">
              <Step
                step={1}
                title="Create your services"
                description="Add services like &ldquo;Full Groom&rdquo;, &ldquo;Bath &amp; Brush&rdquo;, or &ldquo;Nail Trim&rdquo;. Each service gets a unique color for easy identification in your calendar."
              />

              <Step
                step={2}
                title="Set breed-specific pricing"
                description="Configure different prices and durations for All breeds, Small, Medium, Large, or Extra Large breeds to match your business model."
              />

              <Step
                step={3}
                title="Book appointments"
                description="Your services automatically appear when booking appointments, with pricing and duration adjusted based on the pet&apos;s breed size."
                withLine={false}
              />
            </div>
          </Modal.Content>
        </Modal>
      )}
    </>
  )
}
